import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      // Use the full endpoint to get permissions
      axios.get(`${API}/auth/me/full`).then(res => {
        setUser(res.data);
        setPermissions(res.data.permissions || {});
        setLoading(false);
      }).catch(() => {
        localStorage.removeItem("token");
        setToken(null);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    // Main admin has all permissions
    setPermissions(res.data.user.permissions || {
      events_read: true, events_write: true,
      announcements_read: true, announcements_write: true,
      sermons_read: true, sermons_write: true,
      hymns_read: true, hymns_write: true,
      bible_lessons_read: true, bible_lessons_write: true,
      prayers_read: true, prayers_write: true,
      contact_read: true, contact_write: true,
      ushers_access: true,
      attendance_read: true, attendance_write: true,
      finance_read: true, finance_write: true,
      expenses_read: true, expenses_write: true,
      members_read: true, members_write: true,
      birthdays_access: true,
      notifications_access: true,
      exports_download: true
    });
    return res.data;
  };

  const subAdminLogin = async (username, password) => {
    const res = await axios.post(`${API}/auth/sub-admin/login`, { username, password });
    localStorage.setItem("token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    setPermissions(res.data.user.permissions || {});
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setPermissions({});
  };

  const isMainAdmin = user?.role === "admin" || user?.role === "main_admin";
  const isSubAdmin = user?.role === "sub_admin";
  const isAdmin = isMainAdmin || isSubAdmin;
  
  const hasPermission = (perm) => {
    if (isMainAdmin) return true;
    return permissions[perm] === true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, login, subAdminLogin, logout, loading, 
      isAdmin, isMainAdmin, isSubAdmin, 
      permissions, hasPermission 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { API, BACKEND_URL };
